// MapStateStore — zentrales, serialisierbares Map-State-Modell: Single Source of Truth für Logik UND UI.
//
// Prinzip (siehe Plan „Phase State-Objekt"): State = REINE DATEN (Absicht). Keine DOM-Knoten, keine Google-Objekte, keine
// Layer-Instanzen/Closures hier rein. Der Renderer (Layer/Panel/Controls) ist eine PROJEKTION dieses States — Fluss ist
// State → Render (unidirektional). Damit ist der State serialisierbar (Snapshot/Templates/Persistenz/Versionierung) und testbar.
//
// Slice 1 (DORMANT): nur Daten + get/patch + „changed"-Event + snapshot/load. `update_ui` (Render aus dem State) dockt in
// Slice 2 an; per-Kontext-Persistenz + der Reset-vs-Memory-Schalter (`keepOnPositionChange`) folgen in Slice 4.

// Default-State eines Kontexts. Bewusst minimal + flach serialisierbar.
export function createDefaultMapState(context = 'main') {
    return {
        context,
        keepOnPositionChange: false,   // Platzhalter (Reset-vs-Memory) — später granular pro Wert/Overlay
        searchCircleMode: false,       // map-global
        searchCircleActive: false,     // map-global: ist ein Suchkreis gezogen? → steuert den Lösch-Button (Chef-Wunsch 4)
        archivesShown: false,          // map-global
        mapType: null,                 // später neutraler Key (→ Provider-Registry)
        view: null,                    // optional { center, zoom } — für restore/templates
        overlays: []                   // [{ id, visible, opacity, sortOrder, selected }]
    };
}

// Normalisiert ein eingehendes Overlay (z.B. aus get_overlays) auf reine Sicht-/UI-Daten. Stammdaten (name, bounds, path …)
// gehören NICHT in den Store (das ist DAL/DB-Wahrheit) — hier nur der Laufzeit-/UI-Zustand, referenziert per id.
export function normalizeOverlayState(o) {
    return {
        id: o.id,
        visible: !!o.visible,
        opacity: (o.opacity != null) ? o.opacity : 1,
        sortOrder: (o.sortOrder != null) ? o.sortOrder : (o.sort_order != null ? o.sort_order : 0),
        selected: !!o.selected
    };
}

export class MapStateStore {
    constructor(initial) {
        this._state = initial || createDefaultMapState();
        this._listeners = new Set();
    }

    // --- Lesen ---
    get() { return this._state; }
    getOverlay(id) { return this._state.overlays.find(o => o.id === id) || null; }

    // --- Schreiben (immutable; emittiert „changed") ---
    // Flache map-globale Felder patchen (searchCircleMode, archivesShown, mapType, view, …).
    patch(partial) {
        this._state = { ...this._state, ...partial };
        this._emit({ type: 'state', partial });
    }

    // Overlays komplett setzen (z.B. nach get_overlays) — auf reine Sicht-Daten normalisiert.
    setOverlays(overlays) {
        this._state = { ...this._state, overlays: (overlays || []).map(normalizeOverlayState) };
        this._emit({ type: 'overlays' });
    }

    // Einzelnes Overlay patchen (visible/opacity/sortOrder/selected). No-op, wenn die id unbekannt ist.
    patchOverlay(id, partial) {
        let changed = false;
        const overlays = this._state.overlays.map(o => {
            if (o.id !== id) { return o; }
            changed = true;
            return { ...o, ...partial };
        });
        if (!changed) { return; }
        this._state = { ...this._state, overlays };
        this._emit({ type: 'overlay', id, partial });
    }

    // --- Snapshot / Load (Templates, Persistenz, Reset-vs-Memory) ---
    // Tiefe, alias-freie Kopie reiner Daten — taugt als Template, Persistenz-Payload und Versionierungs-Input.
    snapshot() { return JSON.parse(JSON.stringify(this._state)); }

    // Einen Snapshot laden (gespeicherter Kontext-State = Memory, oder Default = Reset).
    load(state) {
        this._state = state || createDefaultMapState(this._state.context);
        this._emit({ type: 'load' });
    }

    // --- Abonnieren ---
    subscribe(fn) {
        this._listeners.add(fn);
        return () => this._listeners.delete(fn);
    }

    _emit(change) {
        for (const fn of this._listeners) { fn(this._state, change); }
    }
}
